import { useState } from 'react';
import dayjs from 'dayjs';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { visibleActionStatusOptions } from 'components/IAP/ICSConstants';
import { useHwcgFlag } from 'utils/featureFlags';
import { PlannedAction } from 'types/iapBaseTypes';

const DIALOG_SURFACE =
  'dark tw:border-[var(--border-primary)] tw:bg-black tw:text-foreground';

const TASK_MAX_LENGTH = 250;

export type PlannedActionEditDrawerProps = {
  /** True when adding a new row; header shows "Add Action" vs "Edit Action". */
  isNewAction: boolean;
  onDismiss: () => void;
  selectedAction: PlannedAction;
  onSave: (selectedAction: PlannedAction) => void;
};

/** `datetime-local` wants 'YYYY-MM-DDTHH:mm' and nothing else. */
const toInputValue = (value: Date | null): string =>
  value && dayjs(value).isValid()
    ? dayjs(value).format('YYYY-MM-DDTHH:mm')
    : '';

const PlannedActionEditDrawer: React.FC<PlannedActionEditDrawerProps> = ({
  isNewAction,
  onDismiss,
  selectedAction,
  onSave,
}) => {
  const [draft, setDraft] = useState<PlannedAction>(selectedAction);
  const statusOptions = visibleActionStatusOptions(useHwcgFlag());

  const set = <K extends keyof PlannedAction>(
    key: K,
    value: PlannedAction[K]
  ) => setDraft((current) => ({ ...current, [key]: value }));

  const taskTooLong = draft.task.length > TASK_MAX_LENGTH;
  // The form has always demanded a name and a status before it will save.
  const saveDisabled = !draft.task.trim() || taskTooLong || !draft.status;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onDismiss();
      }}
    >
      <DialogContent
        className={DIALOG_SURFACE}
        showCloseButton={false}
        data-testid="ics209-planned-action-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {isNewAction ? 'Add Action' : 'Edit Action'}
          </DialogTitle>
        </DialogHeader>

        <div className="tw:grid tw:gap-4">
          <Field>
            <FieldLabel htmlFor="planned-action-task">Name</FieldLabel>
            <Input
              id="planned-action-task"
              maxLength={TASK_MAX_LENGTH}
              value={draft.task}
              onChange={(event) => set('task', event.target.value)}
            />
            {taskTooLong ? (
              <FieldDescription className="tw:text-destructive">
                This maximum length is {TASK_MAX_LENGTH} characters
              </FieldDescription>
            ) : null}
          </Field>

          <Field>
            <FieldLabel htmlFor="planned-action-poc">
              Point of Contact
            </FieldLabel>
            <Input
              id="planned-action-poc"
              value={draft.pointOfContact ?? ''}
              onChange={(event) => set('pointOfContact', event.target.value)}
            />
          </Field>

          <div className="tw:flex tw:items-center tw:gap-2">
            <Checkbox
              id="planned-action-poc-briefed"
              checked={draft.pocBriefed}
              onCheckedChange={(checked) => set('pocBriefed', !!checked)}
            />
            <FieldLabel
              htmlFor="planned-action-poc-briefed"
              className="tw:font-normal"
            >
              Briefed POC
            </FieldLabel>
          </div>

          <Field>
            <FieldLabel htmlFor="planned-action-status">Status</FieldLabel>
            <Select
              value={draft.status ?? ''}
              onValueChange={(value) =>
                set('status', value as PlannedAction['status'])
              }
            >
              <SelectTrigger id="planned-action-status">
                <SelectValue placeholder="Select a status" />
              </SelectTrigger>
              <SelectContent className="dark">
                {statusOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field>
            <FieldLabel htmlFor="planned-action-start">
              Start Date &amp; Time
            </FieldLabel>
            <Input
              id="planned-action-start"
              type="datetime-local"
              value={toInputValue(draft.startDate)}
              onChange={(event) =>
                set(
                  'startDate',
                  event.target.value ? new Date(event.target.value) : null
                )
              }
            />
          </Field>

          <Field>
            <FieldLabel htmlFor="planned-action-deadline">
              Deadline Date &amp; Time
            </FieldLabel>
            <Input
              id="planned-action-deadline"
              type="datetime-local"
              value={toInputValue(draft.deadline)}
              onChange={(event) =>
                set(
                  'deadline',
                  event.target.value ? new Date(event.target.value) : null
                )
              }
            />
          </Field>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onDismiss}>
            Close
          </Button>
          <Button
            type="button"
            disabled={saveDisabled}
            onClick={() => onSave(draft)}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PlannedActionEditDrawer;
